import { tempoApi } from '@/lib/api/tempoApi';
import {
  type BulkCreateTeamsRequest,
  type BulkCreateTeamsResponse,
  type BulkPatchTeamsRequest,
  type BulkTeamActionRequest,
  type BulkTeamActionResponse,
  type CreateTeamRequest,
  type ImportTeamsRequest,
  type ImportTeamsSearch,
  type ImportTeamsSearchParameters,
  type ImportTeamsSearchResponse,
  type MembershipBulkAddRequest,
  type MembershipBulkDeleteRequest,
  type MembershipBulkUpdateRequest,
  type MembershipResponse,
  type ResourceFilterStatus,
  type ResourceTeamMembership,
  type SearchMembershipsSearchParams,
  SORT_DIRECTION,
  type SyncStatusResponse,
  type SyncTeamsRequest,
  TEAM_SORT_FIELD,
  type Team,
  type TeamPatchRequest,
  type TeamSearchParams,
  type TeamsLookupRequest,
  type TeamsLookupResponse,
  type TeamsPageResponse,
  type TeamsResponse,
  type TeamWithIncludedResponse,
} from '@/lib/api/types/teams';

const searchTeams = async ({
  query = '',
  sortBy = TEAM_SORT_FIELD.NAME,
  sortDirection = SORT_DIRECTION.ASC,
  roleIds,
  loopIds,
  limit,
  cursor,
}: TeamSearchParams): Promise<TeamsPageResponse> => {
  const params = new URLSearchParams({ query, sortBy, sortDirection });
  if (limit !== undefined) {
    params.append('limit', String(limit));
  }
  if (cursor !== undefined) {
    params.append('cursor', cursor);
  }
  if (roleIds?.length) {
    params.append('roleIds', roleIds.join(','));
  }
  if (loopIds?.length) {
    params.append('loopIds', loopIds.join(','));
  }
  return await tempoApi()
    .get(`api/v1/capacity/teams/search?${params.toString()}`)
    .json<TeamsPageResponse>();
};

const getTeams = async (params?: { loopIds?: string[] }): Promise<TeamsResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.loopIds?.length) {
    searchParams.append('loopIds', params.loopIds.join(','));
  }
  const query = searchParams.toString();
  return await tempoApi()
    .get(`api/v1/capacity/teams${query ? `?${query}` : ''}`)
    .json<TeamsResponse>();
};

const getTeam = async (
  teamId: number,
  resourceStatus?: ResourceFilterStatus,
): Promise<TeamWithIncludedResponse> => {
  const params = new URLSearchParams({ includeMembers: 'true' });
  if (resourceStatus) {
    params.append('resourceStatus', resourceStatus);
  }
  return await tempoApi()
    .get(`api/v1/capacity/teams/${teamId}?${params.toString()}`)
    .json<TeamWithIncludedResponse>();
};

const searchTeamMembershipsByTeam = async (
  teamId: number,
  search: SearchMembershipsSearchParams | undefined = { query: '' },
): Promise<TeamWithIncludedResponse> => {
  const params = new URLSearchParams({
    query: search.query || '',
    ...(search.resourceStatus && { resourceStatus: search.resourceStatus }),
    ...(search.roleId?.length && { roleIds: search.roleId.join(',') }),
    ...(search.skillId?.length && { skillIds: search.skillId.join(',') }),
    ...(search.locationId?.length && { locationIds: search.locationId.join(',') }),
    ...(search.showPastMemberships !== undefined && {
      showPastMemberships: search.showPastMemberships.toString(),
    }),
    ...(search.showFutureMemberships !== undefined && {
      showFutureMemberships: search.showFutureMemberships.toString(),
    }),
  });

  return await tempoApi()
    .get(`api/v1/capacity/teams/${teamId}/search?${params.toString()}`)
    .json<TeamWithIncludedResponse>();
};

const updateTeam = async ({
  teamId,
  data,
}: {
  teamId: number;
  data: TeamPatchRequest;
}): Promise<Team> => {
  return await tempoApi()
    .patch(`api/v1/capacity/teams/${teamId}`, {
      json: data,
    })
    .json<Team>();
};

const deleteTeam = async (teamId: number): Promise<void> => {
  await tempoApi().delete(`api/v1/capacity/teams/${teamId}`);
};

const createTeam = async (data: CreateTeamRequest): Promise<Team> => {
  return await tempoApi().post('api/v1/capacity/teams', { json: data }).json<Team>();
};

const bulkCreateTeams = async (data: BulkCreateTeamsRequest): Promise<BulkCreateTeamsResponse> => {
  const teams = data.teams.map((team) =>
    team.memberEmails && team.memberEmails.length > 0
      ? { name: team.name, memberEmails: team.memberEmails }
      : { name: team.name },
  );
  return await tempoApi()
    .post('api/v1/capacity/teams/bulk', { json: { teams, loopId: data.loopId } })
    .json<BulkCreateTeamsResponse>();
};

const bulkAddMembersToTeam = async (
  teamId: number,
  resourceIds: number[],
  attributes?: {
    startDate?: string | null;
    endDate?: string | null;
    commitment?: number;
    roleId?: number | null;
  },
): Promise<MembershipResponse[]> => {
  return await tempoApi()
    .post(`api/v1/capacity/team-memberships/team/${teamId}/bulk`, {
      json: { resourceIds, ...attributes },
    })
    .json<MembershipResponse[]>();
};

const getImportTeamsSearch = async ({
  sourceType,
  instanceId,
}: ImportTeamsSearchParameters): Promise<ImportTeamsSearch[]> => {
  const params = new URLSearchParams({
    sourceType,
    instanceId,
  });
  const response = await tempoApi()
    .get(`api/v1/capacity/import/teams/search?${params.toString()}`)
    .json<ImportTeamsSearchResponse>();
  return response.data;
};

const importTeams = async (data: ImportTeamsRequest) => {
  return await tempoApi().post('api/v1/capacity/import/teams', { json: data }).json();
};

const syncTeam = async (teamId: number) => {
  return await tempoApi().post(`api/v1/capacity/teams/${teamId}/sync`).json();
};

const syncTeams = async (data: SyncTeamsRequest) => {
  return await tempoApi().post('api/v1/capacity/teams/sync', { json: data }).json();
};

const getSyncStatus = async () => {
  return await tempoApi().get('api/v1/capacity/sync/status').json<SyncStatusResponse>();
};

const lookupTeams = async (data: TeamsLookupRequest) => {
  return await tempoApi()
    .post('api/v1/capacity/teams/lookup', {
      json: { ...data, expand: ['MEMBERSHIPS', 'CAPACITY'] },
    })
    .json<TeamsLookupResponse>();
};

const getResourceTeamMemberships = async (
  resourceId: number,
): Promise<ResourceTeamMembership[]> => {
  return await tempoApi()
    .get(`api/v1/capacity/team-memberships/resource/${resourceId}`)
    .json<ResourceTeamMembership[]>();
};

const deleteTeamMembership = async (membershipId: number): Promise<void> => {
  await tempoApi().delete(`api/v1/capacity/team-memberships/${membershipId}`);
};

const bulkDeleteTeamMemberships = async (data: MembershipBulkDeleteRequest): Promise<void> => {
  await tempoApi().delete('api/v1/capacity/team-memberships/bulk', {
    json: data,
  });
};

const bulkUpdateTeamMemberships = async (data: MembershipBulkUpdateRequest): Promise<void> => {
  await tempoApi().put('api/v1/capacity/team-memberships/bulk', {
    json: data,
  });
};

const bulkAddMemberships = async (
  resourceId: number,
  data: MembershipBulkAddRequest,
): Promise<ResourceTeamMembership[]> => {
  return await tempoApi()
    .post(`api/v1/capacity/team-memberships/resource/${resourceId}/bulk`, { json: data })
    .json<ResourceTeamMembership[]>();
};

const bulkPatchTeams = async (data: BulkPatchTeamsRequest): Promise<Team[]> => {
  return await tempoApi().patch('api/v1/capacity/teams/bulk', { json: data }).json<Team[]>();
};

const bulkDeleteTeams = async (teamIds: number[]): Promise<void> => {
  await tempoApi().delete('api/v1/capacity/teams/bulk', {
    json: teamIds,
  });
};

const bulkTeamActions = async (data: BulkTeamActionRequest): Promise<BulkTeamActionResponse> => {
  return await tempoApi()
    .post('api/v1/capacity/team-memberships/bulk-actions', { json: data })
    .json<BulkTeamActionResponse>();
};

export const TeamsApi = {
  getTeam,
  getTeams,
  updateTeam,
  deleteTeam,
  createTeam,
  bulkCreateTeams,
  bulkAddMembersToTeam,
  bulkAddMemberships,
  bulkPatchTeams,
  bulkDeleteTeams,
  bulkTeamActions,
  getResourceTeamMemberships,
  bulkUpdateTeamMemberships,
  bulkDeleteTeamMemberships,
  deleteTeamMembership,
  getImportTeamsSearch,
  importTeams,
  searchTeamMembershipsByTeam,
  searchTeams,
  syncTeam,
  syncTeams,
  getSyncStatus,
  lookupTeams,
} as const;
